// src/services/api.js
const API_BASE_URL = 'https://hearing.haruno.design/api';

/**
 * プロジェクト一覧を取得する
 * @param {number} page - ページ番号
 * @param {number} limit - 1ページあたりの件数
 * @returns {Promise<Object>} - 取得データとページネーション情報
 */
export const fetchProjects = async (page = 0, limit = 20) => {
  try {
    const response = await fetch(`${API_BASE_URL}/projects.php?page=${page}&limit=${limit}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'プロジェクトの取得に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * プロジェクト詳細を取得する
 * @param {number} id - プロジェクトID
 * @returns {Promise<Object>} - プロジェクトデータ
 */
export const fetchProjectById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/project.php/${id}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'プロジェクトの取得に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * 新規プロジェクトを作成する
 * @param {Object} data - プロジェクトデータ
 * @returns {Promise<Object>} - 作成結果と新規プロジェクトID
 */
export const createProject = async (data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/projects.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'プロジェクトの作成に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * プロジェクト情報を更新する
 * @param {number} id - プロジェクトID
 * @param {Object} data - 更新データ
 * @returns {Promise<Object>} - 更新結果
 */
export const updateProject = async (id, data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/project.php/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'プロジェクトの更新に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * ヒアリングを開始する
 * @param {number} projectId - プロジェクトID
 * @param {string} content - 相談内容
 * @returns {Promise<Object>} - AIからの応答
 */
export const startHearing = async (projectId, content) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'startHearing',
        projectId,
        content,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'ヒアリングの開始に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * メッセージを送信する
 * @param {number} projectId - プロジェクトID
 * @param {string} message - ユーザーメッセージ
 * @param {Array} previousMessages - これまでのメッセージ履歴
 * @returns {Promise<Object>} - AIからの応答
 */
export const sendMessage = async (projectId, message, previousMessages) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'sendMessage',
        projectId,
        message,
        previousMessages,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'メッセージの送信に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * ヒアリングを終了し要約する
 * @param {number} projectId - プロジェクトID
 * @returns {Promise<Object>} - 要約結果
 */
export const completeHearing = async (projectId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'completeHearing',
        projectId,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'ヒアリングの終了処理に失敗しました');
    }
    
    return await response.json();
  } catch (error) {
    console.error('APIエラー:', error);
    throw error;
  }
};

/**
 * プロジェクトを論理削除する
 * @param {number} id - プロジェクトID
 * @returns {Promise<Object>} - 削除結果
 */
export const deleteProject = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/delete_project.php`, {
        method: 'POST',  // DELETEではなくPOST
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id })  // IDをリクエストボディに含める
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'プロジェクトの削除に失敗しました');
      }
      
      return await response.json();
    } catch (error) {
      console.error('APIエラー:', error);
      throw error;
    }
  };

/**
 * ストリーミングでメッセージを送信する
 * @param {number} projectId - プロジェクトID
 * @param {string} message - ユーザーメッセージ
 * @param {Array} previousMessages - これまでのメッセージ履歴
 * @param {Function} onChunk - 部分的な応答を受け取るコールバック
 * @param {Function} onComplete - 完了時のコールバック
 * @param {Function} onError - エラー時のコールバック
 */
export const sendMessageStreaming = async (
  projectId, 
  message, 
  previousMessages,
  onChunk,
  onComplete,
  onError
) => {
  try {
    // 1. ストリームセッションを作成
    const createResponse = await fetch(`${API_BASE_URL}/create_stream.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectId,
        message,
        previousMessages
      })
    });
    
    if (!createResponse.ok) {
      const errorData = await createResponse.json();
      throw new Error(errorData.error || 'ストリームセッションの作成に失敗しました');
    }
    
    const { streamId } = await createResponse.json();
    
    // 2. ストリーミングを開始
    const eventSource = new EventSource(`${API_BASE_URL}/get_stream.php?id=${streamId}`);
    let fullResponse = '';
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.content) {
          // 受信したコンテンツを即座に処理
          window.requestAnimationFrame(() => {
            fullResponse += data.content;
            onChunk(data.content);
          });
        }
      } catch (error) {
        console.error('Streaming parse error:', error, event.data);
      }
    };
    
    eventSource.addEventListener('done', () => {
      eventSource.close();
      onComplete(fullResponse);
    });
    
    eventSource.addEventListener('error', (event) => {
      console.error('Streaming error:', event);
      eventSource.close();
      
      let errorMessage = 'ストリーミングエラーが発生しました';
      try {
        if (event.data) {
          const data = JSON.parse(event.data);
          if (data.error) {
            errorMessage = data.error;
          }
        }
      } catch (e) {
        console.error('Error parsing error data:', e);
      }
      
      onError(errorMessage);
    });
    
    return eventSource;
  } catch (error) {
    console.error('Stream setup error:', error);
    onError(error.message || 'ストリーミングの設定に失敗しました');
    return { close: () => {} };
  }
};